import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Gauge, Smartphone, Zap, ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/config";

const projects = [
  {
    id: "blade-room",
    title: "Blade Room",
    category: "Barbieri",
    image: "https://images.unsplash.com/photo-1585747860715-2ba37e788b70?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "+42% prenotazioni",
    description: "Sito con prenotazione online 24/7, galleria dei tagli e stile dark premium.",
  },
  {
    id: "gentlemans-cut",
    title: "Gentleman's Cut",
    category: "Barbieri",
    image: "https://images.unsplash.com/photo-1621645582931-d1d3e6564943?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "Agenda sempre piena",
    description: "Landing con booking integrato e lista d'attesa automatica per il weekend.",
  },
  {
    id: "osteria-nonna",
    title: "Osteria Da Nonna",
    category: "Ristoranti",
    image: "https://images.unsplash.com/photo-1467003909585-2f8a72700288?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "+55% tavoli prenotati",
    description: "Menu digitale con QR code, prenotazione tavoli e gallery dei piatti.",
  },
  {
    id: "sushi-kaiten",
    title: "Sushi Kaiten",
    category: "Ristoranti",
    image: "https://images.unsplash.com/photo-1663530761401-15eefb544889?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "Menu in 3 lingue",
    description: "Sito elegante con menu interattivo e ordini da asporto semplificati.",
  },
  {
    id: "atelier-moda",
    title: "Atelier Moda",
    category: "E-commerce",
    image: "https://images.unsplash.com/photo-1620799139507-2a76f79a2f4d?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "+30% vendite online",
    description: "Store di abbigliamento con checkout veloce e lookbook editoriale.",
  },
  {
    id: "studio-locale",
    title: "Studio Nova",
    category: "Local Business",
    image: "https://images.unsplash.com/photo-1651761179569-4ba2aa054997?crop=entropy&cs=srgb&fm=jpg&q=85",
    stat: "1° su Google in città",
    description: "Sito vetrina ottimizzato Local SEO per dominare le ricerche di zona.",
  },
];

const filters = ["Tutti", "Barbieri", "Ristoranti", "E-commerce", "Local Business"];

export const Portfolio = () => {
  const [active, setActive] = useState("Tutti");
  const [selected, setSelected] = useState(null);

  const visible = active === "Tutti" ? projects : projects.filter((p) => p.category === active);

  return (
    <section id="portfolio" className="relative py-24 lg:py-32 bg-[#0F0F14]" data-testid="portfolio-section">
      <div className="mx-auto max-w-7xl px-6">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-12 flex flex-wrap items-end justify-between gap-8"
        >
          <div className="max-w-xl">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-cyan-400 mb-4">Portfolio</p>
            <h2 className="font-syne text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
              Progetti concept. <span className="text-zinc-500">Risultati reali.</span>
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.map((f) => (
              <button
                key={f}
                onClick={() => setActive(f)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-all duration-300 ${
                  active === f
                    ? "bg-cyan-400 text-zinc-950"
                    : "border border-white/10 text-zinc-400 hover:border-cyan-400/40 hover:text-zinc-100"
                }`}
                data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {f}
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => (
              <motion.button
                layout
                key={p.id}
                initial={{ opacity: 0, scale: 0.94 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.94 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                onClick={() => setSelected(p)}
                className="group relative overflow-hidden rounded-2xl border border-white/10 text-left"
                data-testid={`portfolio-card-${p.id}`}
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={p.image}
                    alt={`${p.title} — ${p.category}`}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                  />
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/30 to-transparent opacity-80 transition-opacity duration-500 group-hover:opacity-95" />
                <div className="absolute inset-x-0 bottom-0 p-6">
                  <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">{p.category} · Concept</span>
                  <h3 className="mt-1 font-syne text-xl font-bold">{p.title}</h3>
                  <p className="mt-1 text-sm text-amber-300">{p.stat}</p>
                </div>
                <ArrowUpRight
                  size={22}
                  className="absolute top-5 right-5 text-zinc-300 opacity-0 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1"
                />
              </motion.button>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-center justify-center bg-zinc-950/80 backdrop-blur-md p-4"
            onClick={() => setSelected(null)}
            data-testid="portfolio-modal"
          >
            <motion.div
              initial={{ scale: 0.92, y: 30 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.92, y: 30 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-2xl overflow-hidden rounded-2xl border border-white/10 bg-zinc-900"
            >
              <button
                onClick={() => setSelected(null)}
                className="absolute top-4 right-4 z-10 rounded-full bg-zinc-950/70 p-2 text-zinc-300 hover:text-white transition-colors"
                data-testid="portfolio-modal-close"
                aria-label="Chiudi"
              >
                <X size={18} />
              </button>
              <img src={selected.image} alt={selected.title} className="h-64 w-full object-cover" />
              <div className="p-8">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-300">
                  {selected.category} · Progetto concept
                </span>
                <h3 className="mt-2 font-syne text-2xl font-bold">{selected.title}</h3>
                <p className="mt-3 text-zinc-400 leading-relaxed">{selected.description}</p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {[
                    { icon: Gauge, label: "Lighthouse 100/100" },
                    { icon: Smartphone, label: "100% Mobile" },
                    { icon: Zap, label: "Caricamento < 1s" },
                  ].map((b) => (
                    <span
                      key={b.label}
                      className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-zinc-800/60 px-3 py-1.5 text-xs text-zinc-300"
                    >
                      <b.icon size={14} className="text-cyan-400" />
                      {b.label}
                    </span>
                  ))}
                </div>
                <button
                  onClick={() => {
                    setSelected(null);
                    scrollToId("#contatti");
                  }}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-cyan-400 px-6 py-3 font-semibold text-zinc-950 transition-all duration-300 hover:bg-cyan-300 hover:shadow-[0_0_24px_rgba(56,189,248,0.4)]"
                  data-testid="portfolio-modal-cta"
                >
                  Voglio un sito così
                  <ArrowUpRight size={16} />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
